export type EntityAudience = 'INTERNAL' | 'CLIENT';

/** Glyph keys → unique solid marks in `entityMarkShapes.tsx` (admin chrome SSOT). */
export type EntityIconKey =
  | 'tag'
  | 'shopping-cart'
  | 'lifebuoy'
  | 'user-edit'
  | 'clipboard-text'
  | 'truck-tick'
  | 'people'
  | 'shop'
  | 'document-text'
  | 'box';

export type EntityMentionMeta = {
  description: string;
  aliases: string[];
};

/**
 * Pastel tone key — CSS in `EntityTypeMark.module.css`.
 * Complementary to Hamssah burgundy (`--wp-brunswick-800`) + warm (`--wp-brand-warm`).
 */
export type EntityToneKey =
  | 'catalog'
  | 'commerce'
  | 'quote'
  | 'procure'
  | 'receive'
  | 'partner'
  | 'client'
  | 'support'
  | 'staff'
  | 'neutral';

export type EntityRegistryEntry = {
  type: string;
  iconKey: EntityIconKey;
  tone: EntityToneKey;
  /** Winston slogan handle — short ops verb phrase (FR). */
  slogan: string;
  defaultAudience: EntityAudience;
  href: (id: string) => string | null;
  fallbackLabel: string;
  /** Present → entity appears in `@` mention type picker. */
  mention?: EntityMentionMeta;
};

function encode(id: string): string {
  return encodeURIComponent(id);
}

/**
 * Daily-ops entity archetypes. Change glyph / slogan / href once here.
 * Icon / mark components: `EntityMarkShape` in `entityMarkShapes.tsx`.
 */
const ENTITY_REGISTRY: Record<string, EntityRegistryEntry> = {
  Product: {
    type: 'Product',
    iconKey: 'tag',
    tone: 'catalog',
    slogan: 'Mettre en rayon',
    defaultAudience: 'INTERNAL',
    href: (id) => `/catalog/products/${encode(id)}`,
    fallbackLabel: 'Produit',
    mention: {
      description: 'Référencer un article du catalogue',
      aliases: ['produit', 'product', 'prod'],
    },
  },
  Order: {
    type: 'Order',
    iconKey: 'shopping-cart',
    tone: 'commerce',
    slogan: 'Encaisser',
    defaultAudience: 'INTERNAL',
    href: (id) => `/orders/${encode(id)}`,
    fallbackLabel: 'Commande',
    mention: {
      description: 'Référencer une commande client',
      aliases: ['commande', 'order', 'cmd'],
    },
  },
  B2bSalesOrder: {
    type: 'B2bSalesOrder',
    iconKey: 'shop',
    tone: 'commerce',
    slogan: 'Confirmer B2B',
    defaultAudience: 'INTERNAL',
    href: (id) => `/b2b/sales-orders/${encode(id)}`,
    fallbackLabel: 'Commande B2B',
  },
  B2bDeliveryNote: {
    type: 'B2bDeliveryNote',
    iconKey: 'truck-tick',
    tone: 'commerce',
    slogan: 'Livrer B2B',
    defaultAudience: 'INTERNAL',
    href: () => null,
    fallbackLabel: 'BL B2B',
  },
  B2bCreditNote: {
    type: 'B2bCreditNote',
    iconKey: 'document-text',
    tone: 'quote',
    slogan: 'Avoir B2B',
    defaultAudience: 'INTERNAL',
    href: () => null,
    fallbackLabel: 'Avoir B2B',
  },
  SaleQuote: {
    type: 'SaleQuote',
    iconKey: 'document-text',
    tone: 'quote',
    slogan: 'Proposer',
    defaultAudience: 'INTERNAL',
    href: (id) => `/sales/quotes/${encode(id)}`,
    fallbackLabel: 'Devis client',
    mention: {
      description: 'Référencer un devis showroom',
      aliases: ['devis', 'salequote', 'dv'],
    },
  },
  PurchaseOrder: {
    type: 'PurchaseOrder',
    iconKey: 'clipboard-text',
    tone: 'procure',
    slogan: 'Réapprovisionner',
    defaultAudience: 'INTERNAL',
    href: (id) => `/procurement/purchase-orders/${encode(id)}`,
    fallbackLabel: 'Bon de commande',
    mention: {
      description: 'Référencer un achat fournisseur',
      aliases: ['achat', 'po', 'bc'],
    },
  },
  Batch: {
    type: 'Batch',
    iconKey: 'box',
    tone: 'receive',
    slogan: 'Tracer le lot',
    defaultAudience: 'INTERNAL',
    href: (id) => `/inventory/lots/${encode(id)}`,
    fallbackLabel: 'Lot',
    mention: {
      description: 'Référencer un lot en stock',
      aliases: ['lot', 'batch'],
    },
  },
  Reception: {
    type: 'Reception',
    iconKey: 'truck-tick',
    tone: 'receive',
    slogan: 'Réceptionner',
    defaultAudience: 'INTERNAL',
    href: (id) => `/procurement/purchase-orders?receptionId=${encode(id)}`,
    fallbackLabel: 'Réception',
  },
  Supplier: {
    type: 'Supplier',
    iconKey: 'shop',
    tone: 'partner',
    slogan: 'Sourcing',
    defaultAudience: 'INTERNAL',
    href: (id) => `/procurement/suppliers/${encode(id)}`,
    fallbackLabel: 'Fournisseur',
    mention: {
      description: 'Référencer un fournisseur',
      aliases: ['fournisseur', 'supplier', 'frs'],
    },
  },
  UserProfile: {
    type: 'UserProfile',
    iconKey: 'people',
    tone: 'client',
    slogan: 'Connaître',
    defaultAudience: 'CLIENT',
    href: (id) => `/customer/profiles/${encode(id)}`,
    fallbackLabel: 'Client',
    mention: {
      description: 'Référencer un profil client',
      aliases: ['client', 'profil', 'customer'],
    },
  },
  SignatureEnvelope: {
    type: 'SignatureEnvelope',
    iconKey: 'document-text',
    tone: 'partner',
    slogan: 'Faire signer',
    defaultAudience: 'INTERNAL',
    href: (id) => `/conventions/${encode(id)}`,
    fallbackLabel: 'Convention',
    mention: {
      description: 'Référencer une convention de partenariat',
      aliases: ['convention', 'enveloppe', 'nda'],
    },
  },
  SupportTicket: {
    type: 'SupportTicket',
    iconKey: 'lifebuoy',
    tone: 'support',
    slogan: 'Débloquer',
    defaultAudience: 'CLIENT',
    href: (id) => `/support/${encode(id)}`,
    fallbackLabel: 'Ticket support',
    mention: {
      description: 'Référencer un ticket client',
      aliases: ['ticket', 'support'],
    },
  },
  AuthAccount: {
    type: 'AuthAccount',
    iconKey: 'user-edit',
    tone: 'staff',
    slogan: 'Collaborer',
    defaultAudience: 'CLIENT',
    href: (id) => `/users/${encode(id)}`,
    fallbackLabel: 'Collaborateur',
    mention: {
      description: 'Référencer un compte utilisateur',
      aliases: ['user', 'utilisateur', 'collab'],
    },
  },
};

/** Stable mention picker order (ops gravity). */
const MENTION_ORDER = [
  'Product',
  'Order',
  'SaleQuote',
  'PurchaseOrder',
  'Supplier',
  'SignatureEnvelope',
  'UserProfile',
  'SupportTicket',
  'AuthAccount',
] as const;

export function getEntityRegistryEntry(entityType: string | null | undefined): EntityRegistryEntry | null {
  if (!entityType) return null;
  return ENTITY_REGISTRY[entityType] ?? null;
}

export function listMentionableEntities(): EntityRegistryEntry[] {
  return MENTION_ORDER.map((type) => ENTITY_REGISTRY[type]).filter(
    (e): e is EntityRegistryEntry => Boolean(e?.mention),
  );
}

export function getEntityHref(entityType: string | null | undefined, entityId: string | null | undefined): string | null {
  if (!entityType || !entityId) return null;
  return getEntityRegistryEntry(entityType)?.href(entityId) ?? null;
}

export function getEntityDefaultAudience(
  entityType: string | null | undefined,
  fallback: EntityAudience = 'INTERNAL',
): EntityAudience {
  return getEntityRegistryEntry(entityType)?.defaultAudience ?? fallback;
}

export function getEntitySlogan(entityType: string | null | undefined): string | null {
  return getEntityRegistryEntry(entityType)?.slogan ?? null;
}

export function getEntityTone(entityType: string | null | undefined): EntityToneKey {
  return getEntityRegistryEntry(entityType)?.tone ?? 'neutral';
}

/** Type name only (no id) — for badges / marks. */
export function getEntityTypeName(entityType: string | null | undefined): string {
  return getEntityRegistryEntry(entityType)?.fallbackLabel ?? (entityType?.trim() || 'Contexte');
}

export function getEntityLabel(
  entityType: string | null | undefined,
  entityId: string | null | undefined,
  explicitLabel?: string | null,
): string {
  const trimmed = explicitLabel?.trim();
  if (trimmed) return trimmed;
  const entry = getEntityRegistryEntry(entityType);
  if (!entry) return entityType?.trim() || 'Contexte';
  if (!entityId) return entry.fallbackLabel;
  return `${entry.fallbackLabel} · ${entityId.slice(0, 8)}`;
}
